import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { BusinessesComponent } from './features/admin/businesses/businesses.component';
import { YourCustomersComponent } from './features/provider/your-customers/your-customers.component';
import { CustomerLocationsComponent } from './features/provider/customer-locations/customer-locations.component';
import { CustomerPondsComponent } from './features/provider/customer-ponds/customer-ponds.component';
import { PersonsListComponent } from './features/admin/persons/persons-list.component';
import { CultureSummaryReportComponent } from './features/provider/culture-summary/culture-summary-report.component';
import { LoginComponent } from './components/login/login.component';
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component';
import { TermsofuseComponent } from './components/termsofuse/termsofuse.component';
import { PondsSummaryReportsComponent } from './features/provider/pond-summary/ponds-summary.component';
import { AclSyncComponent } from './features/admin/acl-sync/acl-sync.component';
import { SpeciesTypeComponent } from './features/admin/species-types/species-type.component';
import { CultureCategoryComponent } from './features/admin/culture-categories/culture-category.component';
import { SoilTypeComponent } from './features/admin/soil-types/soil-type.component';
import { BarCodeComponent } from './features/provider/barcodes/bar-code.component';
import { WaterQualityHistoryComponent } from './features/provider/water-quality-history/water-quality-history.component';
import { WaterTypeComponent } from './features/admin/water-types/water-type.component';
import { UnitsOfMeasuresComponent } from './features/admin/units-of-measures/uom.component';
import { ItemTypesComponent } from './features/admin/item-types/item-types.component';
import { FeedComponent } from './features/provider/feed-metrics/feed.component';
import { StockingDensityComponent } from './features/provider/stocking-density-metrics/stocking-density.component';
import { PondComponent } from './features/provider/pond-metrics/pond.component';
import { WaterQualityComponent } from './features/provider/water-quality-metrics/water-quality.component';
import { GrowthComponent } from './features/provider/sampling-metrics/growth.component';
import { HarvestComponent } from './features/provider/harvest-metrics/harvest.component';
import { RoutesComponent } from './features/admin/routes/routes.component';
import { FeatureComponent } from './features/admin/features/feature.component';
import { ScreenComponent } from './features/admin/screens/screen.component';
import { PondTracebilityComponent } from './features/provider/traceability-summary/pond-tracebility.component';
import { DaySummaryComponent } from './features/provider/day-summary/day-summary.component';
import { BusinessCategoryComponent } from './features/admin/business-categories/business-category.component';
import { PersonRoleComponent } from './features/admin/person-roles/person-role.component';
import { BusinessTypeComponent } from './features/admin/business-types/business-type.component';
import { ProductsComponent } from './features/provider/products/products.component';
import { ShapesComponent } from './features/admin/shapes/shapes.component';
import { ActivityTrackerComponent } from './features/provider/activity-tracker/activity-tracker.component';
import { OrdersComponent } from './features/provider/product-orders/orders.component';
import { HomeComponent } from './components/home/home.component';
import { ForgotPasswordStep1Component } from './components/forgot-password-step1/forgot-password-step1.component';
import { ForgotPasswordStep2Component } from './components/forgot-password-step2/forgot-password-step2.component';
import { ResetpasswordComponent } from './components/resetpassword/resetpassword.component';
import { QuickRegistrationComponent } from './features/admin/quick-registration/quick-registration.component';
import { CustomerAccountClosureComponent } from './features/provider/customer-account-closure/customer-account-closure.component';
import { AccountClosureComponent } from './features/admin/account-closure/account-closure.component';
import { VisitsComponent } from './features/provider/visits/visits.component';
import { ProviderDashboardComponent } from './features/provider/provider-dashboard/provider-dashboard.component';
import { TransactionTypeComponent } from './features/admin/transaction-types/transaction-type.component';
import { AdminDashboardComponent } from './features/admin/admin-dashboard/admin-dashboard.component';
import { ActivitiesListComponent } from './features/admin/activities/activities-list.component';
import { BroodBreedersComponent } from './features/admin/brood-breeders/brood-breeders.component';
import { BroodLinesComponent } from './features/admin/brood-lines/brood-lines.component';
import { ObservationTypesComponent } from './features/admin/observation-types/observation-types.component';
import { PondTypesComponent } from './features/admin/pond-types/pond-types.component';
import { PondUsagesComponent } from './features/admin/pond-usages/pond-usages.component';
import { TreatmentTypesComponent } from './features/admin/treatment-types/treatment-types.component';
import { BusinessProfileComponent } from './features/provider/business-profile/business-profile.component';

/**
 * @constant routes
 * @type {Routes}
 * @description
 * Defines the primary routing configuration for the AquaCloud application. This array maps URL paths
 * to their corresponding Angular components, enabling navigation throughout the application.
 *
 * **Key Features:**
 * - **Route Protection**: Utilizes the `AuthGuard` to protect routes that require user authentication.
 * - **Lazy Loading (Future)**: Structured to support lazy loading of feature modules to improve initial load times.
 * - **Role-Based Access (Implicit)**: The structure allows for different views based on user roles, managed by the components themselves or future role-based guards.
 * - **Hierarchical Routing**: Uses a nested `children` array under the `/home` path to create a consistent layout for authenticated users, with a shared `HomeComponent` containing the main navigation.
 *
 * **Route Structure:**
 * 1. **Public Routes**: Accessible to all users without authentication (e.g., `/login`, `/forgotpasswordstep1`).
 * 2. **Authenticated Routes**: Nested under `/home` and protected by `AuthGuard`. These routes represent the core application functionality.
 * 3. **Specialized Routes**: Top-level routes for specific purposes, like `smelandingpage`.
 * 4. **Wildcard Route**: A catch-all route (`**`) that redirects any unmatched URL to the login page, preventing 404 errors.
 *
 * @guard AuthGuard - Protects routes by verifying that a user session exists in `localStorage`. If the check fails, it redirects the user to the `/login` page.
 *
 * @example
 * // A typical protected route definition
 * {
 *   path: 'home',
 *   component: HomeComponent,
 *   canActivate: [AuthGuard], // This route and its children are protected
 *   children: [
 *     { path: 'dashboard', component: BlubayDashboardComponent, canActivate: [AuthGuard] }
 *   ]
 * }
 */
const routes: Routes = [
  // ==========================================================================
  // Public & Redirection Routes
  // ==========================================================================
  { path: '', pathMatch: 'full', redirectTo: '/login' },
  { path: '#/', pathMatch: 'full', redirectTo: '/login' },
  { path: 'login', component: LoginComponent },
  { path: 'forgotpasswordstep1', component: ForgotPasswordStep1Component },
  { path: 'forgotpasswordstep2/:id', component: ForgotPasswordStep2Component },
  { path: 'resetpasswordstep1', component: ResetpasswordComponent },

  // ==========================================================================
  // Main Authenticated Application Layout
  // All child routes are protected by the parent's AuthGuard.
  // ==========================================================================
  {
    path: 'home', component: HomeComponent, canActivate: [AuthGuard],
    children: [
      { path: '', component: AdminDashboardComponent, canActivate: [AuthGuard] },
      { path: 'privacypolicy', component: PrivacyPolicyComponent },
      { path: 'termsofduse', component: TermsofuseComponent },
      { path: 'blubayuserlogin/:id', component: LoginComponent },

      // --------------------------------------------------------------------------
      // Admin Panel: User Settings Masters
      // --------------------------------------------------------------------------
      { path: 'routes', component: RoutesComponent, canActivate: [AuthGuard] },
      { path: 'feature', component: FeatureComponent, canActivate: [AuthGuard] },
      { path: 'screen', component: ScreenComponent, canActivate: [AuthGuard] },
      { path: 'aclsync', component: AclSyncComponent, canActivate: [AuthGuard] },
      { path: 'businesscategory', component: BusinessCategoryComponent, canActivate: [AuthGuard] },
      { path: 'businesstype', component: BusinessTypeComponent, canActivate: [AuthGuard] },
      { path: 'personrole', component: PersonRoleComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Admin Panel: Material Management Masters
      // --------------------------------------------------------------------------
      { path: 'transactiontype', component: TransactionTypeComponent, canActivate: [AuthGuard] },
      { path: 'uom', component: UnitsOfMeasuresComponent, canActivate: [AuthGuard] },
      { path: 'shapes', component: ShapesComponent, canActivate: [AuthGuard] },
      { path: 'itemtype', component: ItemTypesComponent, canActivate: [AuthGuard] },
      { path: 'products', component: ProductsComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Admin Panel: Aqua Culture Masters
      // --------------------------------------------------------------------------
      { path: 'culturecategory', component: CultureCategoryComponent, canActivate: [AuthGuard] },
      { path: 'watertype', component: WaterTypeComponent, canActivate: [AuthGuard] },
      { path: 'soiltype', component: SoilTypeComponent, canActivate: [AuthGuard] },
      { path: 'speciestype', component: SpeciesTypeComponent, canActivate: [AuthGuard] },
      { path: 'activitieslist', component: ActivitiesListComponent, canActivate: [AuthGuard] },
      { path: 'broodbreederslist', component: BroodBreedersComponent, canActivate: [AuthGuard] },
      { path: 'broodlineslist', component: BroodLinesComponent, canActivate: [AuthGuard] },
      { path: 'observationtypeslist', component: ObservationTypesComponent, canActivate: [AuthGuard] },
      { path: 'pondtypeslist', component: PondTypesComponent, canActivate: [AuthGuard] },
      { path: 'pondusageslist', component: PondUsagesComponent, canActivate: [AuthGuard] },
      { path: 'treatmenttypeslist', component: TreatmentTypesComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Dashboard & Details
      // --------------------------------------------------------------------------
      { path: 'dashboard', component: ProviderDashboardComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Reports
      // --------------------------------------------------------------------------
      { path: 'culturesummaryreport', component: CultureSummaryReportComponent, canActivate: [AuthGuard] },
      { path: 'pondsummaryreport', component: PondsSummaryReportsComponent, canActivate: [AuthGuard] },
      { path: 'traceabilityreport', component: PondTracebilityComponent, canActivate: [AuthGuard] },
      { path: 'daysummary', component: DaySummaryComponent, canActivate: [AuthGuard] },
      { path: 'activitytracker', component: ActivityTrackerComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Account Closure
      // --------------------------------------------------------------------------
      { path: 'account-closure/businessID/:id', component: AccountClosureComponent, canActivate: [AuthGuard] },
      { path: 'account-closure', component: CustomerAccountClosureComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Barcode, Orders & Visits Management
      // --------------------------------------------------------------------------
      { path: 'addbarcode', component: BarCodeComponent, canActivate: [AuthGuard] },
      { path: 'orders', component: OrdersComponent, canActivate: [AuthGuard] },
      { path: 'visits', component: VisitsComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Metrics
      // --------------------------------------------------------------------------
      { path: 'farmpond', component: PondComponent, canActivate: [AuthGuard] },
      { path: 'farmstockingdensity', component: StockingDensityComponent, canActivate: [AuthGuard] },
      { path: 'farmfeed', component: FeedComponent, canActivate: [AuthGuard] },
      { path: 'farmharvest', component: HarvestComponent, canActivate: [AuthGuard] },
      { path: 'farmsampling', component: GrowthComponent, canActivate: [AuthGuard] },
      { path: 'farmwaterquality', component: WaterQualityComponent, canActivate: [AuthGuard] },
      { path: 'historywaterquality', component: WaterQualityHistoryComponent, canActivate: [AuthGuard] },

      // --------------------------------------------------------------------------
      // Trading Community & Business Entity Management
      // --------------------------------------------------------------------------
      { path: 'businesses', component: BusinessesComponent, canActivate: [AuthGuard] },
      { path: 'Quick-Registartion', component: QuickRegistrationComponent, canActivate: [AuthGuard] },
      { path: 'customers', component: YourCustomersComponent, canActivate: [AuthGuard] },
      { path: 'customer-locations/:businessID', component: CustomerLocationsComponent, canActivate: [AuthGuard] },
      { path: 'customer-ponds/:locId', component: CustomerPondsComponent, canActivate: [AuthGuard] },
      { path: 'Persons-List/:isAdmin', component: PersonsListComponent, canActivate: [AuthGuard] },
      { path: 'business-profile', component: BusinessProfileComponent, canActivate: [AuthGuard] },

      // Wildcard route for any unknown paths under '/home'
      { path: '**', component: LoginComponent },
    ],
  },
];

/**
 * @module AppRoutingModule
 * @description
 * The main routing module for the application.
 *
 * This module configures the Angular `RouterModule` with all the application's routes.
 * It uses the `forRoot` method because it is the root routing module.
 *
 * **Configuration:**
 * - `useHash: true`: Enables the hash-based routing strategy (`/#/path`). This is often used for compatibility with older browsers or specific server configurations.
 * - `scrollPositionRestoration: 'top'`: Ensures that the user is scrolled to the top of the page upon every navigation.
 */
@NgModule({
  // imports: [RouterModule.forRoot(routes)],
  imports: [RouterModule.forRoot(routes, { useHash: true, scrollPositionRestoration: 'top' })],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
